/**
 * usePeerDataChannel — Send/receive data on a specific peer's data channel.
 *
 * `isOpen` mirrors the channel's real readyState: it is read from the
 * PeerConnection on (re)subscribe and kept up to date through the
 * CHANNEL_OPENED / CHANNEL_CLOSED events and connection state changes.
 */

import { useState, useEffect, useCallback } from "react";
import { useEventBus } from "shared/services/eventBus";
import { ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { useConnectionManager } from "features/robot-teleop/contexts/RobotConnectionManagerContext";

export const usePeerDataChannel = (peerId, channelLabel = "chat") => {
  const { manager } = useConnectionManager();
  const eventBus = useEventBus();
  const [lastMessage, setLastMessage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const readOpen = () => !!(peerId && manager?.getPeer(peerId)?.isChannelOpen(channelLabel));
    setIsOpen(readOpen());
    setLastMessage(null);
    if (!peerId) return undefined;

    const unsubMessage = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.CHANNEL_MESSAGE),
      (msg) => {
        if (msg.channel === channelLabel) {
          setLastMessage(msg);
        }
      }
    );

    const unsubOpened = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.CHANNEL_OPENED),
      (info) => {
        if (info.label === channelLabel) setIsOpen(true);
      }
    );

    const unsubClosed = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.CHANNEL_CLOSED),
      (info) => {
        if (info.label === channelLabel) setIsOpen(false);
      }
    );

    // Belt and braces: whenever the peer's connection state changes, re-read
    // the channel's actual readyState.
    const unsubState = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.CONNECTION_STATE),
      () => setIsOpen(readOpen())
    );

    return () => {
      [unsubMessage, unsubOpened, unsubClosed, unsubState].forEach((unsub) => {
        if (typeof unsub === 'function') unsub();
      });
      setIsOpen(false);
      setLastMessage(null);
    };
  }, [peerId, channelLabel, manager, eventBus]);

  /**
   * Send data on the channel.
   * @returns {boolean} true if the data was handed to an open channel
   */
  const send = useCallback((data) => {
    return !!manager?.getPeer(peerId)?.channelSend(channelLabel, data);
  }, [manager, peerId, channelLabel]);

  return {
    send,
    lastMessage,
    isOpen,
  };
};
